import { useState } from "react";

import { toast } from "react-hot-toast";

import { guardarCotizacion, crearNuevaVersion } from "../../../utils/cotizacionesGuardadas";

/**
 * Hook para guardar cotizaciones desde el cotizador.
 * Recolecta inputs + outputs y los persiste en la tabla cotizaciones.
 */
export const useSaveCotizacionHandler = ({
  // Auth
  userId,
  pais,
  nombre: usuarioNombre,
  apellido: usuarioApellido,
  clienteId: autoClienteId,
  clienteNombre: autoClienteNombre,
  // Inputs
  inventarioData,
  isProgrammaticSelected,
  frecuencia,
  plazo,
  duracion,
  horasSeleccionadas,
  codigoGenerado,
  forceDollars,
  // Discount
  discountType,
  discountPercentage,
  manualDiscountAmount,
  finalAmount,
  // Outputs (from useCalculadoraCalcs)
  ascensoresSeleccionados,
  totemsSeleccionados,
  coworkSeleccionados,
  esperaSeleccionados,
  edificiosUnicosTotal,
  audienciaSeleccionadaTotal,
  audienciaSeleccionadaAscensor,
  audienciaSeleccionadaTotems,
  salidasAscensores,
  salidasTotems,
  salidasTotales,
  impactosTotalAscensores,
  impactosTotalTotems,
  impactosTotales,
  inversionAscensoresFinal,
  inversionTotemsFinal,
  inversionFinalTotal,
  tarifaFriaAscensores,
  tarifaFriaTotems,
  tarifaFriaTotal,
  cpmAscensoresFinal,
  cpmTotemsFinal,
  cpmTotalFinal,
  descuentoGlobal,
  horasFinal,
  // Edit mode (versioning)
  cotizacionGuardadaId,
  navigate,
}) => {
  const [isSaving, setIsSaving] = useState(false);
  const [showGuardarModal, setShowGuardarModal] = useState(false);

  const buildCotizacionData = (nombreCotizacion, clienteIdOverride, clienteNombreOverride) => {
    // Building IDs by circuit (para re-cargar en cotizador al editar)
    const keyMap = {
      ascensores: "edificiosSeleccionadosAscensores",
      totems: "edificiosSeleccionadosTotems",
      cowork: "edificiosSeleccionadosCowork",
      espera: "edificiosSeleccionadosEspera",
    };
    const getEdificioIds = (circuit) => {
      const arr = inventarioData?.[keyMap[circuit]];
      if (!Array.isArray(arr) || arr.length === 0) return "";
      return arr.join(",");
    };

    return {
      // Identificación
      codigo_generado: codigoGenerado,
      nombre_cotizacion: nombreCotizacion || null,
      pais,
      usuario_id: userId,
      usuario_nombre: usuarioNombre,
      usuario_apellido: usuarioApellido,
      // Cliente
      cliente_id: clienteIdOverride || autoClienteId || null,
      cliente_nombre: clienteNombreOverride || autoClienteNombre || null,
      // Estado
      estado: "borrador",
      version: 1,
      version_grupo: codigoGenerado,
      eliminada: false,
      // Inputs
      is_programmatic_selected: isProgrammaticSelected,
      frecuencia: frecuencia ? Number(frecuencia) : null,
      plazo,
      duracion,
      horas: horasSeleccionadas?.horas || 0,
      horas_final: horasFinal || 0,
      intervalos: horasSeleccionadas?.intervalos || [],
      options: horasSeleccionadas?.options || [],
      moneda_dolar: forceDollars || false,
      // Building IDs (inputs para edición futura)
      edificios_ids_ascensores: getEdificioIds("ascensores"),
      edificios_ids_totems: getEdificioIds("totems"),
      edificios_ids_cowork: getEdificioIds("cowork"),
      edificios_ids_espera: getEdificioIds("espera"),
      // Discount inputs
      discount_type: discountType || null,
      discount_percentage: discountPercentage || null,
      manual_discount_amount: manualDiscountAmount || null,
      final_amount: finalAmount || null,
      // Outputs - Edificios/Pantallas
      ascensores_seleccionados: ascensoresSeleccionados,
      totems_seleccionados: totemsSeleccionados,
      edificios_unicos_total: edificiosUnicosTotal,
      // Outputs - Audiencia
      audiencia_seleccionada_total: audienciaSeleccionadaTotal,
      audiencia_seleccionada_ascensor: audienciaSeleccionadaAscensor,
      audiencia_seleccionada_totems: audienciaSeleccionadaTotems,
      // Outputs - Salidas
      salidas_ascensores: salidasAscensores,
      salidas_totems: salidasTotems,
      salidas_totales: salidasTotales,
      // Outputs - Impactos
      impactos_total_ascensores: impactosTotalAscensores,
      impactos_total_totems: impactosTotalTotems,
      impactos_totales: impactosTotales,
      // Outputs - Inversión
      inversion_ascensores_final: inversionAscensoresFinal,
      inversion_totems_final: inversionTotemsFinal,
      inversion_final_total: inversionFinalTotal,
      // Outputs - Tarifa
      tarifa_fria_ascensores: tarifaFriaAscensores,
      tarifa_fria_totems: tarifaFriaTotems,
      tarifa_fria_total: tarifaFriaTotal,
      // Outputs - CPM
      cpm_ascensores_final: cpmAscensoresFinal,
      cpm_totems_final: cpmTotemsFinal,
      cpm_total_final: cpmTotalFinal,
      // Outputs - Descuento
      descuento_total_final: descuentoGlobal,
      // Nombres edificios: no disponibles desde inventarioData (solo IDs)
      nombres_edificios_seleccionados_ascensores: null,
      nombres_edificios_seleccionados_totems: null,
    };
  };

  const handleGuardarCotizacion = async (nombreCotizacion, clienteIdOverride, clienteNombreOverride) => {
    setIsSaving(true);
    try {
      const data = buildCotizacionData(nombreCotizacion, clienteIdOverride, clienteNombreOverride);

      let result;
      if (cotizacionGuardadaId) {
        result = await crearNuevaVersion(cotizacionGuardadaId, data);
        toast.success(`Nueva versión v${result.version} guardada`);
      } else {
        result = await guardarCotizacion(data);
        toast.success("Cotización guardada");
      }

      setShowGuardarModal(false);

      if (navigate) {
        navigate("/admin/mis-cotizaciones");
      }

      return result;
    } catch (err) {
      toast.error("Error al guardar cotización");
      throw err;
    } finally {
      setIsSaving(false);
    }
  };

  return {
    isSaving,
    showGuardarModal,
    setShowGuardarModal,
    handleGuardarCotizacion,
  };
};
